/**
 * The book: searching, filtering, sorting and totalling a company's accounts.
 *
 * Every derived value here already exists somewhere — `overdueAgeing`,
 * `getFollowUpCategory`, `getCustomerPaymentRank`, `matchesSearch`,
 * `matchesSettlement` — and this file arranges them rather than re-deciding
 * anything. That matters: the ageing rule in particular is subtle (a credit
 * note 135 days old is not money 135 days overdue) and was got right once.
 *
 * Pure, so the list's behaviour is testable without a database. What it is
 * **not** is a security boundary: which accounts reach this function is decided
 * by `can_see_customer()` in SQL, and a filter here is a view concern. If this
 * file were deleted, a restricted member would still receive only their own
 * accounts from the server.
 */
import type { AgeingBucket, Customer, PaymentRank, SettlementFilter } from './customer.js';
import { hasOutstanding, matchesSettlement } from './customer.js';
import { getCustomerPaymentRank, getFollowUpCategory, isBadDebt, type FollowUpCategory } from './followUp.js';
import { overdueAgeing, type OverdueAgeing } from './money.js';
import { matchesSearch } from './search.js';
import { daysFromToday } from './format.js';

/* --------------------------------- reading -------------------------------- */

/** One row of the book, with everything the list shows worked out once. */
export interface BookRow {
    customer: Customer;
    overdue: OverdueAgeing;
    followUp: FollowUpCategory;
    rank: PaymentRank;
    badDebt: boolean;
    settled: boolean;
    /** Negative when the follow-up is in the past. Null when none is set. */
    daysToFollowUp: number | null;
    /** Days since anybody recorded anything on the account; null when never. */
    daysSinceContact: number | null;
    /** The account's open promise, or null when there is none. */
    promise: PromiseStanding | null;
}

/**
 * Where an account's open promise stands, in the terms Today counts them.
 *
 * Open means what `worklist_summary()` means: the account is not marked
 * collected and it carries a promised amount above zero (the latest promise,
 * which `record_follow_up()` keeps on the account). Its day is the promised
 * date, else the next follow-up, else today. Nothing here says a promise was
 * kept or broken: no flow records that, and money may have arrived without
 * anybody saying so. "Passed" means only that the promised day has gone.
 */
export interface PromiseStanding {
    amount: number;
    /** Days from the company's today to the promised day; negative once it has gone. */
    days: number;
    window: 'passed' | 'today' | 'week' | 'later';
}

export function promiseStanding(customer: Customer, today: Date = new Date()): PromiseStanding | null {
    const amount = Number(customer.forecastAmount);
    if (customer.status === 'Completed' || !Number.isFinite(amount) || amount <= 0) return null;
    const days = daysFromToday(customer.forecastDate ?? customer.followUpDate ?? null, today) ?? 0;
    const window = days < 0 ? 'passed' : days === 0 ? 'today' : days <= 7 ? 'week' : 'later';
    return { amount, days, window };
}

const midnight = (value: Date): number => {
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
};

const DAY = 86_400_000;

export function bookRow(customer: Customer, today: Date = new Date()): BookRow {
    const followUp = getFollowUpCategory(customer, today);
    let daysToFollowUp: number | null = null;
    if (customer.followUpDate) {
        const due = new Date(customer.followUpDate);
        if (!isNaN(due.getTime())) {
            daysToFollowUp = Math.round((midnight(due) - midnight(today)) / DAY);
        }
    }

    const contacted = customer.lastFollowUpOn ? daysFromToday(customer.lastFollowUpOn, today) : null;

    return {
        customer,
        overdue: overdueAgeing(customer),
        followUp,
        rank: getCustomerPaymentRank(customer),
        badDebt: isBadDebt(customer),
        settled: !hasOutstanding(customer),
        daysToFollowUp,
        daysSinceContact: contacted === null ? null : -contacted,
        promise: promiseStanding(customer, today),
    };
}

/* -------------------------------- filtering ------------------------------- */

/**
 * An ageing window, or one bucket.
 *
 * The windows are what the Book's own filter has always offered. The four
 * `bucket*` values were added with the ageing report, so that pressing a row of
 * it opens exactly the accounts that row counted — one definition, in
 * `book_matches()`, used by the report's number, the report's link and the
 * Book's list.
 */
export type AgeingFilter =
    | 'any' | 'current' | 'over45' | 'over90' | 'over135'
    | 'bucket1' | 'bucket2' | 'bucket3' | 'bucket4'
    // A TallyPrime company's own windows, and what they leave beside them (docs/tally/18 §1.3): a row of the report, opened.
    | AgeingWindowFilter | 'notyetdue' | 'notaged' | 'credits';

export type AgeingWindowFilter = 'window1' | 'window2' | 'window3' | 'window4' | 'window5' | 'window6' | 'window7' | 'window8';
export const AGEING_WINDOW_FILTERS: readonly AgeingWindowFilter[] = ['window1', 'window2', 'window3', 'window4', 'window5', 'window6', 'window7', 'window8'];

/** Whether a filter value is one row of the ageing report rather than an "Older than" window. */
export const isOneAgeRow = (value: AgeingFilter): boolean =>
    value.startsWith('bucket') || value.startsWith('window') || value === 'notyetdue' || value === 'notaged' || value === 'credits';

/** "Older than" as the filter offers it; `current` is "nothing over 45 days". */
export const AGEING_FILTER_LABELS: Partial<Record<AgeingFilter, string>> = {
    any: 'Any age',
    current: 'Under 45 days',
    over45: 'Over 45 days',
    over90: 'Over 90 days',
    over135: 'Over 135 days',
};

/**
 * The ageing bands, in order, each with the filter value that opens it.
 *
 * The keys are `AGEING_BUCKETS` from `customer.ts` — the ones a sheet's columns
 * map to — so there is one set of bands in the product and this only says how
 * to name them and how to ask the Book for one.
 */
export const AGEING_BANDS: readonly { bucket: AgeingBucket; label: string; filter: AgeingFilter }[] = [
    { bucket: '1-45', label: '1 to 45 days', filter: 'bucket1' },
    { bucket: '46-90', label: '46 to 90 days', filter: 'bucket2' },
    { bucket: '91-135', label: '91 to 135 days', filter: 'bucket3' },
    { bucket: '>135', label: 'Over 135 days', filter: 'bucket4' },
];
/**
 * The open promises Today counts, by when they fall — the same four questions
 * `book_matches()` answers in SQL (0032). They ride on the follow-up filter:
 * both are "what needs chasing, and when".
 */
export type PromiseFilter = 'promise_today' | 'promise_week' | 'promise_passed' | 'promise_open' | 'promise_not_kept';
export type FollowUpFilter = FollowUpCategory | PromiseFilter | 'any';

/**
 * `promise_not_kept` is recorded, not inferred (0033): somebody answered the
 * account's latest promise "not kept", and nothing new has been promised or
 * marked collected since. The product still never guesses that a promise was
 * broken from the balance.
 */
export const PROMISE_FILTERS: readonly { value: PromiseFilter; label: string; hint: string }[] = [
    { value: 'promise_today', label: 'Promise due today', hint: 'The customer promised to pay today.' },
    { value: 'promise_week', label: 'Promise due in 7 days', hint: 'Promised for one of the next seven days.' },
    { value: 'promise_passed', label: 'Promise date passed',
        hint: 'The promised day has gone, nobody has answered the promise, and the account is not marked collected.' },
    { value: 'promise_open', label: 'Any open promise', hint: 'Every promise not yet answered or marked collected.' },
    { value: 'promise_not_kept', label: 'Promise not kept',
        hint: 'Somebody recorded that the latest promise was not kept.' },
];

export const isPromiseFilter = (value: string): value is PromiseFilter =>
    PROMISE_FILTERS.some(p => p.value === value);

/** The next-follow-up values, as the filter names them. */
export const FOLLOW_UP_FILTERS: readonly { value: FollowUpCategory; label: string }[] = [
    // The value stays `overdue` (links carry it); the word is "Late", because overdue means money (semantics.ts).
    { value: 'overdue', label: 'Late' },
    { value: 'today', label: 'Due today' },
    { value: 'future', label: 'Scheduled' },
    { value: 'no_follow_up', label: 'Not set' },
    { value: 'completed', label: 'Collected' },
];

const PROMISE_WINDOW: Record<'promise_today' | 'promise_week' | 'promise_passed', PromiseStanding['window']> = {
    promise_today: 'today', promise_week: 'week', promise_passed: 'passed',
};
export type RankFilter = PaymentRank | 'any';
/** A member id, or one of the two answers that are not a member. */
export type OwnerFilter = string;

/** When anybody last recorded anything on the account. */
export type ContactFilter = 'any' | 'week' | 'over7' | 'over30' | 'never';

export const CONTACT_FILTER_LABELS: Record<ContactFilter, string> = {
    any: 'Any time',
    week: 'In the last 7 days',
    over7: 'Not in 7 days',
    over30: 'Not in 30 days',
    never: 'Never',
};

/**
 * The category value that means "none set", in the address bar. In the
 * database it is an empty string; a URL cannot carry an empty repeated value
 * unambiguously.
 */
export const NO_CATEGORY = '(none)';

/**
 * Several values in one filter.
 *
 * `followUp`, `owner` and `rank` carry one value or several joined by commas
 * ("overdue,today") — the form `book_matches()` takes (0033) — and an account
 * matches when it matches any of them. None of their values can contain a
 * comma: they are fixed words or member ids. Categories are the company's own
 * words and can, so they travel as a list.
 */
export const splitList = (value: string | null | undefined): string[] =>
    !value || value === 'any' ? [] : value.split(',').map(v => v.trim()).filter(Boolean);

export const joinList = (values: readonly string[]): string =>
    values.length === 0 ? 'any' : [...new Set(values)].join(',');

export interface BookFilters {
    search: string;
    settlement: SettlementFilter;
    /** One follow-up value, several joined by commas, or `any`. */
    followUp: string;
    ageing: AgeingFilter;
    /** `any`, `unassigned`, `mine`, a member id — or several joined by commas. */
    owner: OwnerFilter;
    /** `any`, a rank, or several joined by commas. */
    rank: string;
    urgentOnly: boolean;
    /** Categories to include; empty means any. `''` is "not set". */
    category: string[];
    contact: ContactFilter;
}

/**
 * The book's resting state: everything that is actually work.
 *
 * `withDues` rather than `all`, because most customers owe nothing. They are
 * real customers and every word of their history matters, but mixed into a
 * worklist they bury the few hundred accounts that are work — "no follow-up
 * set" once read in the thousands when the true answer was a few hundred.
 */
export const DEFAULT_FILTERS: BookFilters = {
    search: '',
    settlement: 'withDues',
    followUp: 'any',
    ageing: 'any',
    owner: 'any',
    rank: 'any',
    urgentOnly: false,
    category: [],
    contact: 'any',
};

export const filtersAreDefault = (filters: BookFilters): boolean =>
    activeFilterCount(filters) === 0;

/** How many filters are narrowing the list, for a "clear filters" affordance. */
export const activeFilterCount = (filters: BookFilters): number =>
    (filters.search.trim() ? 1 : 0)
    + (filters.settlement !== DEFAULT_FILTERS.settlement ? 1 : 0)
    + (filters.followUp !== 'any' ? 1 : 0)
    + (filters.ageing !== 'any' ? 1 : 0)
    + (filters.owner !== 'any' ? 1 : 0)
    + (filters.rank !== 'any' ? 1 : 0)
    + (filters.urgentOnly ? 1 : 0)
    + (filters.category.length > 0 ? 1 : 0)
    + (filters.contact !== 'any' ? 1 : 0);

/**
 * What a search looks at.
 *
 * The company's own code is included because it is what people read off a
 * sheet; the imported owner code because "whose accounts are these" is a
 * question asked of the book before anybody has been assigned.
 */
const searchableParts = (c: Customer): (string | null | undefined)[] => [
    c.company, c.sourceKey, c.contactPerson, c.contactNumber,
    c.email, c.city, c.state, c.taxId, c.category, c.importedOwnerCode,
];

export interface OwnerContext {
    /** The member looking at the book, so `mine` means something. */
    memberId?: string;
}

export function matchesBookFilters(
    row: BookRow,
    filters: BookFilters,
    context: OwnerContext = {},
): boolean {
    const c = row.customer;

    if (!matchesSettlement(c, filters.settlement)) return false;
    if (filters.urgentOnly && !c.isUrgent) return false;

    const followUps = splitList(filters.followUp);
    if (followUps.length > 0 && !followUps.some(value => matchesFollowUp(row, value))) return false;

    const ranks = splitList(filters.rank);
    if (ranks.length > 0 && !ranks.includes(row.rank)) return false;

    if (filters.ageing !== 'any') {
        if (filters.ageing === 'current') {
            if (!hasOutstanding(c) || c.totalType === 'Cr' || row.overdue.over45 > 0) return false;
        } else {
            // book_matches() in SQL is the twin of this: a window reads the account's own windows (0036).
            const w = c.ageing.windows;
            const windowAt = /^window[1-8]$/.test(filters.ageing) ? Number(filters.ageing.slice(6)) - 1 : -1;
            const amount = filters.ageing === 'over45' ? row.overdue.over45
                : filters.ageing === 'over90' ? row.overdue.over90
                    : filters.ageing === 'over135' ? row.overdue.over135
                        : filters.ageing === 'bucket1' ? row.overdue.a1
                            : filters.ageing === 'bucket2' ? row.overdue.a2
                                : filters.ageing === 'bucket3' ? row.overdue.a3
                                    : filters.ageing === 'bucket4' ? row.overdue.a4
                                        : filters.ageing === 'notyetdue' ? w?.notYetDue ?? 0
                                            : filters.ageing === 'notaged' ? w?.notAged ?? 0
                                                : filters.ageing === 'credits' ? w?.credits ?? 0
                                                    : windowAt >= 0 ? w?.amounts[windowAt] ?? 0
                                                        : 0;
            if (amount <= 0) return false;
        }
    }

    const owners = splitList(filters.owner);
    if (owners.length > 0) {
        const owner = c.ownerMemberId as string | undefined;
        const collector = c.collectorMemberId as string | undefined;
        const me = context.memberId;
        const hit = owners.some(value =>
            value === 'unassigned' ? !owner && !collector
                : value === 'mine' ? Boolean(me) && (owner === me || collector === me)
                    : owner === value || collector === value);
        if (!hit) return false;
    }

    if (filters.category.length > 0 && !filters.category.includes((c.category ?? '').trim())) return false;

    if (filters.contact !== 'any') {
        const since = row.daysSinceContact;
        const ok = filters.contact === 'never' ? since === null
            : filters.contact === 'week' ? since !== null && since <= 7
                : filters.contact === 'over7' ? since === null || since > 7
                    : since === null || since > 30;
        if (!ok) return false;
    }

    return matchesSearch(searchableParts(c), filters.search);
}

/** One follow-up value against one account — `book_matches()`'s rule, in TypeScript. */
function matchesFollowUp(row: BookRow, value: string): boolean {
    if (value === 'promise_not_kept') {
        return row.customer.promiseOutcome === 'not_kept' && row.followUp !== 'completed';
    }
    if (isPromiseFilter(value)) {
        if (!row.promise) return false;
        return value === 'promise_open' || row.promise.window === PROMISE_WINDOW[value as keyof typeof PROMISE_WINDOW];
    }
    return row.followUp === value;
}

export function filterBook(
    rows: readonly BookRow[],
    filters: BookFilters,
    context: OwnerContext = {},
): BookRow[] {
    return rows.filter(row => matchesBookFilters(row, filters, context));
}

/**
 * The filters, read out of the address bar.
 *
 * Anything unrecognised falls back to the default rather than being trusted: a
 * pasted link is somebody else's, and a filter value is not validated anywhere
 * else.
 *
 * It lives here rather than on the screen because it is the other half of
 * `BookFilters` — and because the worklist builds links the book has to parse,
 * so a test can prove a card opens exactly the question it counted.
 */
export function filtersFromParams(params: URLSearchParams): BookFilters {
    const oneOf = <T extends string>(value: string | null, allowed: readonly T[], fallback: T): T =>
        allowed.includes((value ?? '') as T) ? (value as T) : fallback;
    // Several values: each checked on its own, the unknown ones dropped.
    const listOf = (value: string | null, allowed: readonly string[]): string =>
        joinList(splitList(value).filter(v => allowed.includes(v)));

    return {
        search: params.get('search') ?? '',
        settlement: oneOf(params.get('settlement'), ['withDues', 'owes', 'credit', 'settled', 'all'] as const, 'withDues'),
        followUp: listOf(
            params.get('followUp'),
            ['today', 'future', 'overdue', 'no_follow_up', 'completed',
             'promise_today', 'promise_week', 'promise_passed', 'promise_open', 'promise_not_kept'],
        ),
        ageing: oneOf(
            params.get('ageing'),
            ['any', 'current', 'over45', 'over90', 'over135',
             'bucket1', 'bucket2', 'bucket3', 'bucket4', ...AGEING_WINDOW_FILTERS, 'notyetdue', 'notaged', 'credits'] as const,
            'any',
        ),
        owner: joinList(splitList(params.get('owner'))),
        rank: listOf(params.get('rank'), ['Good', 'Late', 'Bad']),
        urgentOnly: params.get('urgentOnly') === 'true',
        category: [...new Set(params.getAll('category').map(v => (v === NO_CATEGORY ? '' : v.trim())))]
            .filter(v => v.length <= 120),
        contact: oneOf(params.get('contact'), ['any', 'week', 'over7', 'over30', 'never'] as const, 'any'),
    };
}

/**
 * The filters written into the address bar: only what differs from the
 * resting state, so a plain list has a plain address and a link carries just
 * the question it asks.
 */
export function writeFilterParams(
    params: URLSearchParams,
    changes: Partial<BookFilters> & { sort?: BookSort },
): URLSearchParams {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
        if (key === 'category') {
            next.delete('category');
            for (const category of (value as string[])) next.append('category', category === '' ? NO_CATEGORY : category);
            continue;
        }
        const isDefault =
            value === '' || value === false || value === 'any' || value === undefined
            || (key === 'settlement' && value === DEFAULT_FILTERS.settlement)
            || (key === 'sort' && value === DEFAULT_SORT);
        if (isDefault) next.delete(key); else next.set(key, String(value));
    }
    return next;
}

/* --------------------------------- sorting -------------------------------- */

export type BookSort =
    | 'amount-desc'
    | 'amount-asc'
    | 'oldest-first'
    | 'follow-up'
    | 'name'
    | 'recently-updated';

export const SORT_LABELS: Record<BookSort, string> = {
    'amount-desc': 'Largest amount',
    'amount-asc': 'Smallest amount',
    'oldest-first': 'Oldest money',
    'follow-up': 'Follow-up date',
    'name': 'Name',
    'recently-updated': 'Recently updated',
};

export const DEFAULT_SORT: BookSort = 'amount-desc';

const amountOf = (row: BookRow): number => Math.abs(Number(row.customer.total) || 0);

const time = (value: Date | string | null | undefined): number => {
    if (!value) return 0;
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? 0 : d.getTime();
};

/**
 * Sorted, stably, and never mutating what it was given.
 *
 * "Follow-up date" puts the accounts with no date **last** rather than first.
 * A blank sorts before everything if it is treated as zero, which put every
 * untouched account at the top of the one list meant to be worked in order.
 */
export function sortBook(rows: readonly BookRow[], sort: BookSort): BookRow[] {
    const sorted = [...rows];
    const byName = (a: BookRow, b: BookRow) =>
        a.customer.company.localeCompare(b.customer.company, undefined, { sensitivity: 'base' });

    switch (sort) {
        case 'amount-desc':
            sorted.sort((a, b) => amountOf(b) - amountOf(a) || byName(a, b));
            break;
        case 'amount-asc':
            sorted.sort((a, b) => amountOf(a) - amountOf(b) || byName(a, b));
            break;
        case 'oldest-first':
            // The oldest money first: what has been outstanding longest is what
            // is least likely to arrive on its own.
            sorted.sort((a, b) =>
                b.overdue.over135 - a.overdue.over135
                || b.overdue.over90 - a.overdue.over90
                || b.overdue.over45 - a.overdue.over45
                || byName(a, b));
            break;
        case 'follow-up':
            sorted.sort((a, b) => {
                const av = a.daysToFollowUp;
                const bv = b.daysToFollowUp;
                if (av === null && bv === null) return byName(a, b);
                if (av === null) return 1;
                if (bv === null) return -1;
                return av - bv || byName(a, b);
            });
            break;
        case 'name':
            sorted.sort(byName);
            break;
        case 'recently-updated':
            sorted.sort((a, b) =>
                time(b.customer.lastFollowUpOn) - time(a.customer.lastFollowUpOn) || byName(a, b));
            break;
    }
    return sorted;
}

/* --------------------------------- totals --------------------------------- */

export interface BookTotals {
    count: number;
    /** Net receivable across the rows shown. Credits count down. */
    outstanding: number;
    over90: number;
    dueToday: number;
    overdue: number;
    unassigned: number;
}

/**
 * What the rows on screen add up to.
 *
 * Over the **filtered** rows on purpose: a total that ignores the filter
 * answers a question nobody asked, and invites somebody to read a company-wide
 * figure off a screen showing one collector's accounts.
 */
export function bookTotals(rows: readonly BookRow[]): BookTotals {
    let outstanding = 0;
    let over90 = 0;
    let dueToday = 0;
    let overdue = 0;
    let unassigned = 0;

    for (const row of rows) {
        const amount = Math.abs(Number(row.customer.total) || 0);
        outstanding += row.customer.totalType === 'Cr' ? -amount : amount;
        over90 += row.overdue.over90;
        if (row.followUp === 'today') dueToday += 1;
        if (row.followUp === 'overdue') overdue += 1;
        if (!row.customer.ownerMemberId && !row.customer.collectorMemberId) unassigned += 1;
    }

    return { count: rows.length, outstanding, over90, dueToday, overdue, unassigned };
}

/**
 * The whole view, in one call: derive, filter, sort, total.
 *
 * One function so a screen cannot do the three steps in a different order from
 * the tests — totalling before filtering, in particular, is an easy mistake and
 * produces a number that looks plausible.
 */
export function buildBook(
    customers: readonly Customer[],
    filters: BookFilters,
    sort: BookSort,
    context: OwnerContext = {},
    today: Date = new Date(),
): { rows: BookRow[]; totals: BookTotals; totalAvailable: number } {
    const all = customers.map(c => bookRow(c, today));
    const matching = filterBook(all, filters, context);
    return {
        rows: sortBook(matching, sort),
        totals: bookTotals(matching),
        totalAvailable: all.length,
    };
}
